import { useState } from "react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { X, Wand2 } from "lucide-react";

const SIZE_TEMPLATES = {
  ws_small: { label: "Womenswear XS–XL", sizes: ["XS", "S", "M", "L", "XL"] },
  ws_plus: { label: "Womenswear Plus XL–4XL", sizes: ["XL", "XXL", "3XL", "4XL"] },
  ms: { label: "Menswear S–XXL", sizes: ["S", "M", "L", "XL", "XXL"] },
  eu_num: { label: "EU numeric 34–46", sizes: ["34", "36", "38", "40", "42", "44", "46"] },
  us_num: { label: "US numeric 0–12", sizes: ["0", "2", "4", "6", "8", "10", "12"] },
};

export function TechpackWizard({ open, onClose, mode, design, collection, onCreated }) {
  const [template, setTemplate] = useState("ws_small");
  const [baseSize, setBaseSize] = useState("M");
  const [selectedItems, setSelectedItems] = useState(() => {
    if (collection) return new Set(collection.items.map((it) => it.item_id));
    return new Set();
  });
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const sizes = SIZE_TEMPLATES[template].sizes;
  const effectiveBase = sizes.includes(baseSize) ? baseSize : sizes[Math.floor(sizes.length / 2)];

  const toggle = (itemId) => {
    const next = new Set(selectedItems);
    if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
    setSelectedItems(next);
  };

  const submit = async () => {
    setLoading(true);
    try {
      if (mode === "single") {
        const { data } = await api.post("/techpacks/generate", {
          design_id: design.id,
          version_id: design.version_id || null,
          size_template: template,
          base_size: effectiveBase,
        });
        toast.success("Techpack drafted");
        onCreated?.(data);
      } else {
        const ids = Array.from(selectedItems);
        if (ids.length === 0) {
          toast.error("Select at least one item");
          setLoading(false);
          return;
        }
        const { data } = await api.post("/techpacks/bulk-generate", {
          collection_id: collection.id,
          item_ids: ids,
          size_template: template,
          base_size: effectiveBase,
        });
        toast.success(`Created ${data.created.length} techpack${data.created.length !== 1 ? "s" : ""}${data.failed ? ` (${data.failed} failed)` : ""}`);
        onCreated?.(data);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4" data-testid="techpack-wizard">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Wand2 size={18} className="text-zinc-950" />
            <h2 className="font-heading text-lg font-bold">
              {mode === "single" ? "Create techpack" : "Generate techpacks from collection"}
            </h2>
          </div>
          <button onClick={onClose} data-testid="wizard-close" className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><X size={16} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {mode === "collection" && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-2">Select looks to include ({selectedItems.size}/{collection.items.length})</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-64 overflow-y-auto pr-1">
                {collection.items.map((it) => {
                  const checked = selectedItems.has(it.item_id);
                  return (
                    <button key={it.item_id} onClick={() => toggle(it.item_id)} data-testid={`wizard-item-${it.item_id}`}
                      className={`relative border-2 rounded-md overflow-hidden transition-colors ${checked ? "border-zinc-950" : "border-transparent hover:border-zinc-300"}`}>
                      <img src={imgSrc(it.image)} alt={it.design.title} className="w-full h-24 object-cover" />
                      <p className="text-[10px] p-1 truncate">{it.design.title}{it.version_label ? ` · ${it.version_label}` : ""}</p>
                      <span className={`absolute top-1 right-1 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center text-[10px] ${checked ? "bg-zinc-950 text-white" : "bg-white/70"}`}>
                        {checked ? "✓" : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-1">Size range</p>
              <select data-testid="wizard-template" value={template} onChange={(e) => {
                setTemplate(e.target.value);
                const s = SIZE_TEMPLATES[e.target.value].sizes;
                if (!s.includes(baseSize)) setBaseSize(s[Math.floor(s.length / 2)]);
              }}
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                {Object.entries(SIZE_TEMPLATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-1">Base size</p>
              <select data-testid="wizard-base-size" value={effectiveBase} onChange={(e) => setBaseSize(e.target.value)}
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3">
            AI (Gemini) will analyze the render{mode === "collection" ? "s" : ""} and pre-fill the description, colorways, BOM, workmanship notes and estimated measurements for base size <b>{effectiveBase}</b>. Everything remains editable before export.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 px-5 py-3">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-md hover:bg-zinc-100">Cancel</button>
          <button data-testid="wizard-submit" onClick={submit} disabled={loading}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
            <Wand2 size={14} /> {loading ? "Drafting…" : (mode === "single" ? "Draft techpack" : `Draft ${selectedItems.size} techpack${selectedItems.size !== 1 ? "s" : ""}`)}
          </button>
        </div>
      </div>
    </div>
  );
}

function imgSrc(path) {
  return path?.startsWith("/api/") ? `${BACKEND_URL}${path}` : path;
}
